import { UserTrainingStats } from '@/src/domain/models/TrainingLaunch';
import { useTranslation } from '@/src/i18n';
import { StyleSheet, Text, View } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';

interface StatRowProps {
  icon: string;
  label: string;
  value: string;
  colors: { text: string; textSecondary: string; primary: string };
}

function StatRow({ icon, label, value, colors }: StatRowProps) {
  return (
    <View style={styles.statRow}>
      <MaterialCommunityIcons name={icon as any} size={18} color={colors.primary} style={styles.statIcon} />
      <Text style={[styles.statLabel, { color: colors.textSecondary }]}>{label}</Text>
      <Text style={[styles.statValue, { color: colors.text }]}>{value}</Text>
    </View>
  );
}

interface TrainingStatsCardProps {
  stats: UserTrainingStats;
  colors: {
    card: string;
    border: string;
    text: string;
    textSecondary: string;
    primary: string;
  };
}

export function TrainingStatsCard({ stats, colors }: TrainingStatsCardProps) {
  const { t } = useTranslation();

  return (
    <View style={[styles.card, { backgroundColor: colors.card, borderColor: colors.border }]}>
      <View style={styles.header}>
        <MaterialCommunityIcons name="rotate-right" size={20} color={colors.primary} />
        <Text style={[styles.title, { color: colors.text }]}>
          {t('training.title', { defaultValue: 'Training' })}
        </Text>
      </View>

      <StatRow
        icon="speedometer"
        label={t('training.bestRpm', { defaultValue: 'Best RPM' })}
        value={stats.bestRpm > 0 ? Math.round(stats.bestRpm).toString() : '—'}
        colors={colors}
      />
      <StatRow
        icon="counter"
        label={t('training.totalLaunches', { defaultValue: 'Launches' })}
        value={stats.totalLaunches.toString()}
        colors={colors}
      />
      <StatRow
        icon="chart-line"
        label={t('training.avgConsistency', { defaultValue: 'Avg Consistency' })}
        value={stats.avgConsistency > 0 ? `${Math.round(stats.avgConsistency)}%` : '—'}
        colors={colors}
      />
      <StatRow
        icon="star"
        label={t('training.avgScore', { defaultValue: 'Avg Score' })}
        value={stats.avgScore > 0 ? Math.round(stats.avgScore).toString() : '—'}
        colors={colors}
      />
      <StatRow
        icon="racing-helmet"
        label={t('training.bestSpeed', { defaultValue: 'Top Speed' })}
        value={stats.bestSpeedKmh > 0 ? `${stats.bestSpeedKmh.toFixed(1)} km/h` : '—'}
        colors={colors}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    borderRadius: 12,
    borderWidth: 1,
    padding: 16,
    marginBottom: 16,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
  },
  title: {
    fontSize: 16,
    fontWeight: '600',
  },
  statRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
  },
  statIcon: {
    width: 26,
  },
  statLabel: {
    flex: 1,
    fontSize: 14,
  },
  statValue: {
    fontSize: 14,
    fontWeight: '600',
  },
});
